import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import {
  Box,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileKind(file: File): 'image' | 'video' | 'other' {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return 'other';
}

import { useCreateFileMutation } from 'api/mutations/useCreateFileMutation';
import { useCategoriesListQuery } from 'api/queries/useCategoriesListQuery';
import { useFoldersQuery } from 'api/queries/useFoldersQuery';
import { SelectController } from 'components';
import { useWindowSize } from 'hooks';
import { theme } from 'styles';
import { isSystemCategory } from 'validations';
// import { AccessLevel } from 'types';

import {
  CancelButton,
  Dialog,
  DuplicateButton,
  FooterActions,
  StyledTextField,
} from '../FolderEditCategoryModal/styled';

// const ACCESS_LEVEL_LABELS: Record<string, string> = {
//   [AccessLevel.public]: 'Public',
//   [AccessLevel.shared]: 'Shared',
//   [AccessLevel.private]: 'Private',
// };

interface CreateFileModalProps {
  open: boolean;
  onClose: () => void;
  categoryId?: string;
  folderId?: string;
}

export function CreateFileModal({ open, onClose, categoryId, folderId }: CreateFileModalProps) {
  const [fileName, setFileName] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  // const [accessLevel, setAccessLevel] = useState<AccessLevel>(AccessLevel.public);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const { control, watch, setValue } = useForm<{ categoryIds: string[] }>({
    defaultValues: { categoryIds: categoryId ? [categoryId] : [] },
  });
  const watchedCategoryIds = watch('categoryIds');
  const selectedCategoryIds = useMemo(() => watchedCategoryIds ?? [], [watchedCategoryIds]);

  useEffect(() => {
    if (folderId) {
      setSelectedFolderId(folderId);
    }
  }, [folderId]);

  useEffect(() => {
    setValue('categoryIds', categoryId ? [categoryId] : []);
  }, [categoryId, setValue]);

  // Re-enforce locked category if user deletes its chip
  useEffect(() => {
    if (categoryId && !selectedCategoryIds.includes(categoryId)) {
      setValue('categoryIds', [categoryId, ...selectedCategoryIds]);
    }
  }, [selectedCategoryIds, categoryId, setValue]);

  useEffect(() => {
    if (!file || getFileKind(file) !== 'image') {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const { categoriesList } = useCategoriesListQuery();
  const { createFileMutation } = useCreateFileMutation();
  const { isMobile } = useWindowSize();

  const singleCategoryId = selectedCategoryIds.length === 1 ? selectedCategoryIds[0] : undefined;
  const { folders } = useFoldersQuery({ categoryId: singleCategoryId });

  const handleClose = () => {
    setFileName('');
    setValue('categoryIds', categoryId ? [categoryId] : []);
    // setAccessLevel(AccessLevel.public);
    setFile(null);
    setPreviewUrl(null);
    setSelectedFolderId(folderId ?? null);
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      if (!fileName) setFileName(selected.name);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) {
      setFile(dropped);
      if (!fileName) setFileName(dropped.name);
    }
  };

  const handleSubmit = async () => {
    if (!fileName.trim() || selectedCategoryIds.length === 0) return;

    try {
      const formData = new FormData();
      formData.append('display_name', fileName.trim());
      selectedCategoryIds.forEach((id) => formData.append('category_ids', id));
      if (selectedFolderId) formData.append('folder_id', selectedFolderId);
      // formData.append('access_level', accessLevel);
      if (file) formData.append('file', file);
      await createFileMutation(formData);
    } catch (error) {
      // Handle error (e.g., show notification)
      console.error('Error creating file:', error);
    }
    handleClose();
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.secondary.dark,
    marginBottom: '6px',
    display: 'block',
  };

  const selectStyle = {
    borderRadius: '8px',
    fontSize: '14px',
    height: '56px',
    background: theme.palette.white.main,
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${theme.palette.primary.grey}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${theme.palette.primary.grey}`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${theme.palette.info.main}`,
    },
  };

  const formContent = (
    <Box sx={{ px: isMobile ? 0 : undefined, pb: 'env(safe-area-inset-bottom)' }}>
      <Typography variant="h6" fontWeight={700} fontSize="16px" mb={3}>
        {t('resources.documentCategories.files.createModal.title')}
      </Typography>

      <Box mb={2}>
        <Typography component="span" sx={labelStyle}>
          {t('resources.documentCategories.files.createModal.fileName')}
        </Typography>
        <StyledTextField
          fullWidth
          placeholder={t('resources.documentCategories.files.createModal.placeholders.fileName')}
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
        />
      </Box>

      <Box mb={2}>
        <Typography component="span" sx={labelStyle}>
          {t('resources.documentCategories.files.createModal.selectCategory')}
        </Typography>
        <SelectController
          name="categoryIds"
          control={control}
          placeholder={t(
            'resources.documentCategories.files.createModal.placeholders.selectCategory',
          )}
          options={categoriesList
            .filter((category) => category.tag && !isSystemCategory(category.tag))
            .map((cat) => ({
              value: cat.id,
              label: cat.title,
              disabled: cat.id === categoryId,
            }))}
          multipleSelect
          type="poll"
          handleChange={() => {
            if (selectedCategoryIds.length !== 1) setSelectedFolderId(null);
          }}
          sx={{
            height: 'auto',
            minHeight: '44px',
            borderRadius: '4px',
            '.MuiOutlinedInput-notchedOutline': { border: 'none' },
            '.MuiSelect-select.MuiSelect-select': {
              height: 'auto',
              padding: '8px 0px 8px 0px',
              '& > div': { margin: 0 },
            },
            '.MuiSvgIcon-root.MuiSelect-icon': { top: '50%', transform: 'translateY(-50%)' },
          }}
        />
      </Box>

      {selectedCategoryIds.length === 1 && (
        <Box mb={2}>
          <Typography component="span" sx={labelStyle}>
            {t('resources.documentCategories.files.createModal.selectFolder')}
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={selectedFolderId ?? ''}
            onChange={(e: SelectChangeEvent<string>) => {
              if (e.target.value !== folderId) setSelectedFolderId(e.target.value || null);
            }}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography color="text.disabled" fontSize="14px">
                    {t('resources.documentCategories.files.createModal.placeholders.selectFolder')}
                  </Typography>
                );
              }
              const folder = folders.find((f) => f.id === value);
              return folder?.name ?? value;
            }}
            sx={selectStyle}
          >
            {!folderId && (
              <MenuItem value="">
                <Typography color="text.disabled" fontSize="14px">
                  {t('resources.documentCategories.files.createModal.placeholders.selectFolder')}
                </Typography>
              </MenuItem>
            )}
            {folders.length === 0 && (
              <MenuItem value="" disabled>
                {t('resources.documentCategories.files.createModal.placeholders.emptyFolders')}
              </MenuItem>
            )}
            {folders.map((folder) => (
              <MenuItem
                key={folder.id}
                value={folder.id}
                disabled={folder.id === folderId}
                sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
              >
                <Typography fontSize="14px">{folder.name}</Typography>
                {folder.path && (
                  <Typography fontSize="11px" color="text.disabled" lineHeight={1.2}>
                    {folder.path}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {/* <Box mb={2}>
        <Typography component="span" sx={labelStyle}>
          Manage access
        </Typography>
        <Select
          fullWidth
          value={accessLevel}
          onChange={(e: SelectChangeEvent<string>) => setAccessLevel(e.target.value as AccessLevel)}
          sx={selectStyle}
        >
          {Object.values(AccessLevel).map((level) => (
            <MenuItem key={level} value={level}>
              {ACCESS_LEVEL_LABELS[level]}
            </MenuItem>
          ))}
        </Select>
      </Box> */}

      <Box mb={1}>
        <Typography component="span" sx={labelStyle}>
          {t('resources.documentCategories.files.createModal.placeholders.attachments')}
        </Typography>
        <Box
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: `1.5px dashed ${theme.palette.primary.grey}`,
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            position: 'relative',
          }}
        >
          {file ? (
            <>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  setPreviewUrl(null);
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: theme.palette.white.main,
                  border: `1px solid ${theme.palette.primary.grey}`,
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>

              {getFileKind(file) === 'image' && previewUrl && (
                <Box
                  component="img"
                  src={previewUrl}
                  alt={file.name}
                  sx={{
                    maxHeight: 80,
                    maxWidth: '100%',
                    objectFit: 'contain',
                    borderRadius: '6px',
                  }}
                />
              )}

              {getFileKind(file) === 'video' && (
                <VideoFileIcon sx={{ fontSize: 44, color: theme.palette.secondary.light }} />
              )}

              {getFileKind(file) === 'other' && (
                <InsertDriveFileIcon sx={{ fontSize: 44, color: theme.palette.secondary.light }} />
              )}

              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  fontSize="14px"
                  fontWeight={500}
                  color={theme.palette.secondary.dark}
                  noWrap
                >
                  {file.name}
                </Typography>
                <Typography fontSize="12px">{formatFileSize(file.size)}</Typography>
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  background: theme.palette.info.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FileUploadIcon sx={{ color: theme.palette.white.main, fontSize: 22 }} />
              </Box>
              <Typography
                fontSize="14px"
                color={theme.palette.info.main}
                fontWeight={500}
                sx={{ textDecoration: 'underline' }}
              >
                {t('resources.documentCategories.files.createModal.placeholders.uploadFile')}
              </Typography>
            </>
          )}
          <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
        </Box>
      </Box>

      <FooterActions>
        <CancelButton variant="outlined" onClick={handleClose}>
          {t('resources.documentCategories.files.createModal.buttons.cancel')}
        </CancelButton>
        <DuplicateButton
          variant="contained"
          onClick={handleSubmit}
          disabled={!fileName.trim() || selectedCategoryIds.length === 0 || !file?.name}
        >
          {t('resources.documentCategories.files.createModal.buttons.create')}
        </DuplicateButton>
      </FooterActions>
    </Box>
  );

  return isMobile ? (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '20px 20px 0 0',
          pb: 'env(safe-area-inset-bottom)',
          paddingBottom: '16px',
          px: 2,
          maxHeight: '90dvh',
          overflowY: 'auto',
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 4,
          borderRadius: '2px',
          mx: 'auto',
          my: '12px',
        }}
      />
      {formContent}
    </Drawer>
  ) : (
    <Dialog open={open} onClose={handleClose}>
      {formContent}
    </Dialog>
  );
}
